import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { UpdateAccountImportProfileGQL } from '@wb/core/schema/mutation-definitions/accounts.graphql';
import { UpdateAccountBillingProfileGQL } from '@wb/core/schema/mutation-definitions/billing-profiles.graphql';
import { UpdateSharedImportProfileGQL } from '@wb/core/schema/mutation-definitions/system.graphql';
import { take } from 'rxjs/operators';

export enum ProfileType {
  BILLING = 'billing',
  IMPORT = 'import',
  SHARED_IMPORT = 'shared_import',
}

@Component({
  selector: 'app-secret-dialog',
  templateUrl: './secret-dialog.component.html',
  styleUrls: ['./secret-dialog.component.sass'],
})
export class SecretDialogComponent implements OnInit {
  @Input() username?: string;

  @Input() password?: string;

  @Input() type?: string = 'sftp';

  @Input() accountProfileUid?: string;

  @Input() sharedProfileUid?: string;

  @Input() profileUid?: string;

  @Input() profileType?: ProfileType;

  selectedProfileIsShared: boolean = false;

  dialogTitle: string = '';

  public hostname: string = 'sftp.webbuy.dev';

  formGroup: FormGroup = new FormGroup({
    profile: new FormControl('', [Validators.required]),
    sharedFeedId: new FormControl(''),
  });

  showPassword: boolean = false;

  private snackbarRef?: MatSnackBarRef<any>;

  constructor(
    private dialogRef: MatDialogRef<SecretDialogComponent>,
    private updateAccountImportProfileGQL: UpdateAccountImportProfileGQL,
    private updateAccountBillingProfileGQL: UpdateAccountBillingProfileGQL,
    private updateSharedImportProfileGQL: UpdateSharedImportProfileGQL,
    private readonly snackbar: MatSnackBar,
    private readonly translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit() {
    this.dialogTitle = this.translate.instant('accountSettings.accountSetup.inventorySalesFeed.inventory.secret.title');

    if (this.data.username) {
      this.username = this.data.username;
    }

    if (this.data.password) {
      this.password = this.data.password;
    }

    if (this.data.type) {
      this.type = this.data.type;
      if (this.type === 'ftps') {
        this.hostname = 'ftps.webbuy.dev';
        this.dialogTitle = this.translate.instant(
          'accountSettings.accountSetup.inventorySalesFeed.inventory.secret.titleFTPS',
        );
      }
    }

    if (this.data.accountProfileUid) {
      this.accountProfileUid = this.data.accountProfileUid;
    }

    if (this.data.sharedProfileUid) {
      this.sharedProfileUid = this.data.sharedProfileUid;
    }

    if (this.data.profileUid) {
      this.profileUid = this.data.profileUid;
    }

    if (this.data.profileType) {
      this.profileType = this.data.profileType;
    }

    this.formGroup.controls.profile.patchValue(this.profileUid);

    if (this.data.sharedProfileId) {
      this.formGroup.controls.sharedFeedId.patchValue(this.data.sharedProfileId);
    }

    if (this.data.profiles && this.data.profiles.length > 0) {
      const profile = this.data.profiles.find((p: any) => p.uid === this.profileUid);
      if (profile && profile.isShared) {
        this.selectedProfileIsShared = true;
      }
    }
  }

  isValid(): boolean {
    if (this.selectedProfileIsShared && this.formGroup.controls.sharedFeedId.value) {
      return this.formGroup.controls.sharedFeedId.value.length > 0;
    }

    return this.formGroup.controls.profile.value !== this.profileUid;
  }

  copyToClipboard(text?: string): void {
    if (text) {
      navigator.clipboard.writeText(text);
      this.snackbarRef = this.snackbar.open(
        this.translate.instant('accountSettings.accountSetup.inventorySalesFeed.inventory.secret.copied'),
        '',
        {
          duration: 5000,
        },
      );
    }
  }

  onProfileChange(event: string): void {
    const profile = this.data.profiles.find((p: any) => p.uid === event);

    if (profile && profile.isShared) {
      this.selectedProfileIsShared = true;
    } else {
      this.selectedProfileIsShared = false;
    }
  }

  updateAccountProfile(): void {
    if (this.accountProfileUid && this.formGroup.controls.profile.valid) {
      switch (this.profileType) {
        case ProfileType.BILLING:
          this.updateAccountBillingProfileGQL
            .mutate({ uid: this.accountProfileUid, profileUid: this.formGroup.controls.profile.value })
            .pipe(take(1))
            .subscribe((response) => {
              if (response) {
                this.dialogRef.close({ profile: this.formGroup.controls.profile.value });
              }
            });
          break;
        case ProfileType.IMPORT:
        default:
          this.updateAccountImportProfileGQL
            .mutate({
              uid: this.accountProfileUid,
              importProfileUid: this.formGroup.controls.profile.value,
              sharedProfileId: this.selectedProfileIsShared ? this.formGroup.controls.sharedFeedId.value : '', // If profile is not shared, remove any ID
            })
            .pipe(take(1))
            .subscribe((response) => {
              if (response) {
                this.dialogRef.close({ profile: this.formGroup.controls.profile.value });
              }
            });
          break;
      }
    } else if (
      this.profileType === ProfileType.SHARED_IMPORT &&
      this.sharedProfileUid &&
      this.formGroup.controls.profile.valid
    ) {
      this.updateSharedImportProfileGQL
        .mutate({ uid: this.sharedProfileUid, importProfileUid: this.formGroup.controls.profile.value })
        .pipe(take(1))
        .subscribe((response) => {
          if (response) {
            this.dialogRef.close({ profile: this.formGroup.controls.profile.value });
          }
        });
    } else if (this.formGroup.controls.profile.valid) {
      this.dialogRef.close({ profile: this.formGroup.controls.profile.value });
    }
  }

  togglePasswordView(): void {
    this.showPassword = !this.showPassword;
  }
}
